/**
 * CSV Export Utilities
 * 
 * This module provides centralized CSV export functionality for the platform.
 * It handles data transformation, CSV generation, and file download.
 */

import Papa from "papaparse";
import { CardData, FolderGroup } from "@/components/layout/element-layout/grid-layout/GridLayout";

/**
 * Strip HTML tags from a string
 * Converts HTML content to plain text for CSV export
 */
export function stripHtmlTags(html: string): string {
  if (!html || typeof html !== 'string') return '';
  
  // Create a temporary DOM element to parse HTML
  // This handles HTML entities correctly (e.g., &amp; -> &)
  if (typeof DOMParser !== 'undefined') {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.body.textContent || '';
    } catch {
      // Fallback to regex if DOMParser fails
    }
  }
  
  // Fallback: Use regex to strip HTML tags
  return html
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces
    .replace(/&amp;/g, '&')  // Replace ampersand
    .replace(/&lt;/g, '<')   // Replace less than
    .replace(/&gt;/g, '>')   // Replace greater than
    .replace(/&quot;/g, '"') // Replace quotes
    .replace(/&#39;/g, "'")  // Replace apostrophe
    .replace(/\s+/g, ' ')    // Normalize whitespace
    .trim();
}

/**
 * Sanitize a value for CSV export by stripping HTML tags
 */
export function sanitizeValueForCsv(value: any): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    return value.map(v => stripHtmlTags(String(v))).join(', ');
  }
  if (typeof value === 'object') {
    if (value.text) return stripHtmlTags(value.text);
    if (value.url) return value.url;
    return JSON.stringify(value);
  }
  return stripHtmlTags(String(value));
}

// Column key to display name mapping for metadata columns
export const METADATA_COLUMN_DISPLAY_NAMES: Record<string, string> = {
  'name': 'Name',
  'title': 'Title',
  'description': 'Description',
  'folder': 'Folder',
  'owner': 'Owner',
  'tags': 'Tags',
  'status': 'Status',
  'lastUpdate': 'Last Update',
  'identifiers': 'Identifiers',
  'blueprint': 'Blueprint',
  'blueprint_version': 'Blueprint Version',
  'version': 'Version',
  'type': 'Type',
  'dialog_key': 'Dialog Key',
};

/**
 * Configuration for CSV export
 */
export interface CsvExportConfig {
  /** Function to resolve folder ID to display path */
  folderPathLookup?: (folderId: string | null | undefined) => string;
  /** Map of blueprint IDs to their display names */
  blueprintsMap?: Map<string, string>;
}

/**
 * Get a field value from raw item data
 */
export function getFieldValueFromRawItem(rawItem: any, fieldId: string): string {
  if (!rawItem?.data || !Array.isArray(rawItem.data)) return "";
  
  for (const section of rawItem.data) {
    if (!section.fields || !Array.isArray(section.fields)) continue;
    
    const field = section.fields.find((f: any) => f.id === fieldId);
    if (field) {
      return sanitizeValueForCsv(field.value);
    }
  }
  return "";
}

/**
 * Get the display value for a metadata column from a card
 */
export function getCardMetadataValue(
  card: CardData,
  columnKey: string,
  config: CsvExportConfig = {}
): string {
  const { folderPathLookup, blueprintsMap } = config;
  
  switch (columnKey) {
    case 'name':
    case 'title':
      return stripHtmlTags(card.title || "");
    case 'description':
      return stripHtmlTags(card.description || "");
    case 'owner':
      return stripHtmlTags(card.owner || "");
    case 'folder':
      return folderPathLookup?.(card.folder as string) || "";
    case 'tags':
      return Array.isArray(card.tags) ? card.tags.map(t => stripHtmlTags(t)).join(", ") : "";
    case 'status':
      return stripHtmlTags(card.status || "");
    case 'lastUpdate':
      return card.lastUpdate || "";
    case 'identifiers':
      return Array.isArray(card.identifiers) ? (card.identifiers as string[]).map(i => stripHtmlTags(i)).join(", ") : "";
    case 'blueprint': {
      const blueprintId = card.blueprint_id as string;
      return stripHtmlTags((blueprintId && blueprintsMap?.get(blueprintId)) || blueprintId || "");
    }
    case 'blueprint_version':
      return stripHtmlTags((card.blueprintVersion as string) || "");
    case 'version':
      return String(card.version || "");
    case 'type':
      return stripHtmlTags((card.type as string) || "");
    case 'dialog_key':
      return stripHtmlTags((card.dialog_key as string) || "");
    default:
      return "";
  }
}

/**
 * Find the label for a field ID from raw items
 */
export function findFieldLabel(rawItems: any[], fieldId: string): string {
  for (const item of rawItems) {
    if (Array.isArray(item.data)) {
      for (const section of item.data) {
        if (Array.isArray(section.fields)) {
          const field = section.fields.find((f: any) => f.id === fieldId);
          if (field) {
            return field.label || fieldId;
          }
        }
      }
    }
  }
  return fieldId;
}

/**
 * Parse selected columns into metadata and field columns
 */
export function parseSelectedColumns(
  selectedColumns: string[],
  rawItems: any[]
): {
  headers: string[];
  metadataColumns: string[];
  fieldColumns: string[];
} {
  const headers: string[] = [];
  const metadataColumns: string[] = [];
  const fieldColumns: string[] = [];
  
  selectedColumns.forEach(col => {
    if (col.startsWith('field_')) {
      const fieldId = col.replace('field_', '');
      fieldColumns.push(fieldId);
      headers.push(findFieldLabel(rawItems, fieldId));
    } else {
      const displayName = METADATA_COLUMN_DISPLAY_NAMES[col] || col;
      metadataColumns.push(col);
      headers.push(displayName);
    }
  });
  
  return { headers, metadataColumns, fieldColumns };
}

/**
 * Transform cards to CSV row data
 */
export function transformCardsToRows(
  cards: CardData[],
  rawItems: any[],
  metadataColumns: string[],
  fieldColumns: string[],
  headers: string[],
  config: CsvExportConfig = {}
): Record<string, string>[] {
  return cards.map(card => {
    const rawItem = rawItems.find((item: any) => item.id === card.id);
    const row: Record<string, string> = {};
    
    // Add metadata columns
    metadataColumns.forEach(col => {
      const displayName = METADATA_COLUMN_DISPLAY_NAMES[col] || col;
      row[displayName] = getCardMetadataValue(card, col, config);
    });
    
    // Add field columns
    fieldColumns.forEach((fieldId, index) => {
      const fieldLabel = headers[metadataColumns.length + index];
      row[fieldLabel] = getFieldValueFromRawItem(rawItem, fieldId);
    });
    
    return row;
  });
}

/**
 * Generate CSV string from data
 */
export function generateCsvString(
  data: Record<string, string>[],
  headers: string[]
): string {
  return Papa.unparse(data, {
    columns: headers,
  });
}

/**
 * Download a CSV file
 */
export function downloadCsvFile(csvContent: string, fileName: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Sanitize a filename by replacing special characters
 */
export function sanitizeFileName(name: string): string {
  return name.replace(/[^a-z0-9]/gi, '_');
}

/**
 * Export data for a single folder group
 */
export interface ExportFolderGroupOptions {
  /** The folder group to export */
  group: FolderGroup;
  /** Selected columns to include in export */
  selectedColumns: string[];
  /** Raw items with full data */
  rawItems: any[];
  /** Set of selected row IDs (for filtered export) */
  selectedRows?: Set<string>;
  /** Whether to export only selected rows */
  exportSelectedOnly?: boolean;
  /** Export configuration */
  config?: CsvExportConfig;
}

/**
 * Export a single folder group to CSV
 */
export function exportFolderGroupToCsv(options: ExportFolderGroupOptions): void {
  const {
    group,
    selectedColumns,
    rawItems,
    selectedRows = new Set(),
    exportSelectedOnly = false,
    config = {},
  } = options;
  
  // Determine which cards to export
  let cardsToExport = group.cards;
  if (exportSelectedOnly && selectedRows.size > 0) {
    cardsToExport = group.cards.filter(card => selectedRows.has(card.id));
  }
  
  // Parse columns
  const { headers, metadataColumns, fieldColumns } = parseSelectedColumns(selectedColumns, rawItems);
  
  // Transform to rows
  const csvData = transformCardsToRows(
    cardsToExport,
    rawItems,
    metadataColumns,
    fieldColumns,
    headers,
    config
  );
  
  // Generate and download
  const csv = generateCsvString(csvData, headers);
  const fileName = `${sanitizeFileName(group.folderName)}_export.csv`;
  downloadCsvFile(csv, fileName);
}

/**
 * Export data for multiple folder groups
 */
export interface ExportAllFolderGroupsOptions {
  /** The folder groups to export */
  groups: FolderGroup[];
  /** Function to get selected columns for a folder key */
  getSelectedColumnsForGroup: (folderKey: string) => string[];
  /** Function to get raw items for a folder group */
  getRawItemsForGroup: (group: FolderGroup) => any[];
  /** Map of folder keys to selected row IDs */
  selectedRowsByGroup?: Record<string, Set<string>>;
  /** Whether to export only selected rows */
  exportSelectedOnly?: boolean;
  /** Export configuration */
  config?: CsvExportConfig;
  /** Custom file name (default: 'all_folders_export.csv') */
  fileName?: string;
}

/**
 * Export all folder groups to a single CSV
 */
export function exportAllFolderGroupsToCsv(options: ExportAllFolderGroupsOptions): void {
  const {
    groups,
    getSelectedColumnsForGroup,
    getRawItemsForGroup,
    selectedRowsByGroup = {},
    exportSelectedOnly = false,
    config = {},
    fileName = 'all_folders_export.csv',
  } = options;
  
  const allData: Record<string, string>[] = [];
  const allHeaders = new Set<string>();
  allHeaders.add('Folder');
  
  groups.forEach(group => {
    const folderKey = group.folderId || "unfiled";
    const selectedColumns = getSelectedColumnsForGroup(folderKey);
    const groupRawItems = getRawItemsForGroup(group);
    const groupSelectedRows = selectedRowsByGroup[folderKey] || new Set<string>();
    
    // Determine which cards to export
    let cardsToExport = group.cards;
    if (exportSelectedOnly && groupSelectedRows.size > 0) {
      cardsToExport = group.cards.filter(card => groupSelectedRows.has(card.id));
    }
    
    cardsToExport.forEach(card => {
      const rawItem = groupRawItems.find((item: any) => item.id === card.id);
      const row: Record<string, string> = {
        'Folder': group.folderName,
      };
      
      selectedColumns.forEach(col => {
        if (col.startsWith('field_')) {
          const fieldId = col.replace('field_', '');
          const fieldLabel = findFieldLabel(groupRawItems, fieldId);
          allHeaders.add(fieldLabel);
          row[fieldLabel] = getFieldValueFromRawItem(rawItem, fieldId);
        } else {
          const displayName = METADATA_COLUMN_DISPLAY_NAMES[col] || col;
          allHeaders.add(displayName);
          row[displayName] = getCardMetadataValue(card, col, config);
        }
      });
      
      allData.push(row);
    });
  });
  
  // Generate CSV with all headers (Folder first, then others)
  const headers = ['Folder', ...Array.from(allHeaders).filter(h => h !== 'Folder')];
  const csv = generateCsvString(allData, headers);
  downloadCsvFile(csv, fileName);
}

/**
 * Generic table data export
 */
export interface ExportTableDataOptions {
  /** Array of data rows */
  data: Record<string, any>[];
  /** Column keys to include */
  columns: string[];
  /** Optional column label mapping */
  columnLabels?: Record<string, string>;
  /** File name for download */
  fileName: string;
}

/**
 * Export generic table data to CSV
 */
export function exportTableDataToCsv(options: ExportTableDataOptions): void {
  const { data, columns, columnLabels = {}, fileName } = options;
  
  const headers = columns.map(col => columnLabels[col] || col);
  
  const csvData = data.map(row => {
    const csvRow: Record<string, string> = {};
    columns.forEach((col, index) => {
      const value = row[col];
      const header = headers[index];
      csvRow[header] = sanitizeValueForCsv(value);
    });
    return csvRow;
  });
  
  const csv = generateCsvString(csvData, headers);
  downloadCsvFile(csv, fileName);
}
